import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { CheckCircle, XCircle, Clock } from 'lucide-react';
import { SectionLabel, RefreshBtn, STATUS_ICON, timeAgo, lagColor, heapColor } from './shared';

// ── Service Status ────────────────────────────────────────────────────────────

function ServiceStatusSection({ theme }) {
  const [services, setServices] = useState([]);
  const [checking, setChecking] = useState(false);
  const [checkedAt, setCheckedAt] = useState(null);

  const check = useCallback(() => {
    setChecking(true);
    axios.get('/api/admin/status')
      .then(res => { setServices(res.data); setCheckedAt(new Date()); })
      .catch(() => toast.error('Status check failed'))
      .finally(() => setChecking(false));
  }, []);

  useEffect(() => { check(); }, [check]);

  const [sendingTest, setSendingTest] = useState(false);
  const sendTestEmail = () => {
    setSendingTest(true);
    axios.post('/api/admin/email-test')
      .then(() => toast.success('Test email sent to your address'))
      .catch(err => toast.error(err.response?.data?.error || 'Test email failed'))
      .finally(() => { setSendingTest(false); check(); });
  };

  return (
    <div style={{ marginBottom: 32 }}>
      <SectionLabel theme={theme} action={
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {checkedAt && <span style={{ fontSize: 11, color: theme.textMuted }}>{checkedAt.toLocaleTimeString('en-GB')}</span>}
          <RefreshBtn onClick={check} loading={checking} theme={theme} />
        </div>
      }>
        Service Status
      </SectionLabel>
      <div style={{ borderRadius: 8, border: `1px solid ${theme.border}`, background: theme.surface, overflow: 'hidden' }}>
        {services.length === 0 && !checking && (
          <div style={{ padding: '14px 16px', fontSize: 13, color: theme.textMuted }}>No data</div>
        )}
        {services.map((svc, i) => (
          <div key={svc.service} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '11px 16px', borderBottom: i < services.length - 1 ? `1px solid ${theme.border}` : 'none' }}>
            <span style={{ flexShrink: 0 }}>{STATUS_ICON[svc.status] || STATUS_ICON.warn}</span>
            <span style={{ fontWeight: 600, fontSize: 13, color: theme.text, minWidth: 90 }}>{svc.service}</span>
            <span style={{ fontSize: 12, color: theme.textMuted, flex: 1 }}>{svc.message || ''}</span>
            {svc.latencyMs != null && <span style={{ fontSize: 11, color: theme.textMuted, fontFamily: 'monospace', flexShrink: 0 }}>{svc.latencyMs}ms</span>}
            {svc.testable && (
              <button
                onClick={sendTestEmail}
                disabled={sendingTest}
                style={{ flexShrink: 0, padding: '4px 10px', borderRadius: 6, border: `1px solid ${theme.border}`, background: theme.surfaceAlt, color: theme.text, fontSize: 12, cursor: 'pointer' }}
              >
                {sendingTest ? 'Sending…' : 'Send test email'}
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Health strip ──────────────────────────────────────────────────────────────

function SummaryHealthStrip({ theme }) {
  const [platform, setPlatform] = useState(null);
  const [ws, setWs]             = useState(null);

  useEffect(() => {
    const load = () => {
      Promise.all([
        axios.get('/api/admin/platform-stats').then(r => setPlatform(r.data)),
        axios.get('/api/admin/ws-metrics').then(r => setWs(r.data)),
      ]).catch(() => {});
    };
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, []);

  const heapPct = platform
    ? Math.round(parseFloat(platform.node.heapUsedMb) / parseFloat(platform.node.heapTotalMb) * 100)
    : null;
  const lagMs    = platform ? parseFloat(platform.node.eventLoopLagMs) : null;
  const cacheHit = platform?.db.cacheHitRate;
  const uptime   = platform ? (() => {
    const s = Math.floor(platform.node.uptimeSec);
    const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60);
    return h > 0 ? `${h}h ${m}m` : `${m}m`;
  })() : '—';

  const cacheColor = cacheHit == null ? '#6c757d' : cacheHit >= 95 ? '#22c55e' : cacheHit >= 85 ? '#f59e0b' : '#ef4444';

  const cards = [
    { label: 'Heap',      value: heapPct != null ? `${heapPct}%`      : '—', color: heapColor(heapPct) },
    { label: 'Event loop',value: lagMs   != null ? `${lagMs}ms`       : '—', color: lagColor(lagMs) },
    { label: 'DB cache',  value: cacheHit != null ? `${cacheHit}%`    : '—', color: cacheColor },
    { label: 'WS live',   value: ws?.live.totalConnected ?? '—' },
    { label: 'Uptime',    value: uptime },
  ];

  return (
    <div style={{ marginBottom: 32 }}>
      <SectionLabel theme={theme}>Node health</SectionLabel>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(110px, 1fr))', gap: 10 }}>
        {cards.map(c => (
          <div key={c.label} style={{ background: theme.surfaceAlt, borderRadius: 8, border: `1px solid ${theme.border}`, padding: '12px 14px' }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: theme.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>{c.label}</div>
            <div style={{ fontSize: 20, fontWeight: 700, color: c.color || theme.text, lineHeight: 1 }}>{c.value}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Platform KPIs ─────────────────────────────────────────────────────────────

function HeadlineStats({ theme }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    axios.get('/api/admin/analytics')
      .then(r => setData(r.data.headlines))
      .catch(() => {});
  }, []);

  const stats = data ? [
    { label: 'Tenants',          value: data.total_tenants },
    { label: 'Requests (total)', value: data.total_requests },
    { label: 'Gigs (total)',     value: data.total_gigs },
    { label: 'Active gigs',      value: data.active_gigs },
    { label: 'Requests (7d)',    value: data.requests_this_week },
    { label: 'Songs (total)',    value: data.total_songs },
  ] : [];

  return (
    <div style={{ marginBottom: 32 }}>
      <SectionLabel theme={theme}>Platform overview</SectionLabel>
      {!data ? (
        <div style={{ fontSize: 13, color: theme.textMuted }}>Loading…</div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 10 }}>
          {stats.map(({ label, value }) => (
            <div key={label} style={{ background: theme.surfaceAlt, borderRadius: 8, border: `1px solid ${theme.border}`, padding: '14px 16px' }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: theme.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>{label}</div>
              <div style={{ fontSize: 28, fontWeight: 700, color: theme.text, lineHeight: 1 }}>{value ?? '—'}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Cron jobs ─────────────────────────────────────────────────────────────────

const JOB_LABELS = {
  'auto-gig-scheduler': { label: 'Auto-gig scheduler', schedule: 'Every 60s' },
  'auto-archive':       { label: 'Auto-archive gigs',  schedule: 'Every 1h'  },
  'backup':             { label: 'DB backup',           schedule: 'Daily'     },
  'log-cleanup':        { label: 'Log cleanup',         schedule: 'Daily'     },
  'account-cleanup':    { label: 'Unconfirmed sign-ups', schedule: 'Daily'    },
};

function CronJobsSection({ theme }) {
  const [jobs, setJobs]       = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    axios.get('/api/admin/cron-jobs')
      .then(res => setJobs(res.data))
      .catch(() => toast.error('Failed to load cron jobs'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const allJobs = Object.entries(JOB_LABELS).map(([key, meta]) => ({
    key, ...meta, log: jobs.find(j => j.job_name === key),
  }));

  return (
    <div style={{ marginBottom: 32 }}>
      <SectionLabel theme={theme} action={<RefreshBtn onClick={load} loading={loading} theme={theme} />}>
        Cron Jobs
      </SectionLabel>
      <div style={{ borderRadius: 8, border: `1px solid ${theme.border}`, overflow: 'hidden' }}>
        {allJobs.map((job, i) => {
          const result = job.log?.result;
          const summary = result ? Object.entries(result).map(([k, v]) => `${k}: ${v}`).join(' · ') : null;
          return (
            <div key={job.key} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', background: theme.surface, borderBottom: i < allJobs.length - 1 ? `1px solid ${theme.border}` : 'none' }}>
              <span style={{ flexShrink: 0 }}>
                {job.log == null
                  ? <Clock size={16} color={theme.textMuted} />
                  : job.log.ok ? <CheckCircle size={16} color="#22c55e" /> : <XCircle size={16} color="#ef4444" />}
              </span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: theme.text }}>{job.label}</div>
                {summary && <div style={{ fontSize: 11, color: theme.textMuted, marginTop: 2 }}>{summary}</div>}
              </div>
              <div style={{ textAlign: 'right', flexShrink: 0 }}>
                <div style={{ fontSize: 12, color: job.log ? theme.text : theme.textMuted }}>{job.log ? timeAgo(job.log.ran_at) : 'no runs yet'}</div>
                <div style={{ fontSize: 11, color: theme.textMuted, marginTop: 2 }}>{job.schedule}</div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Tab ───────────────────────────────────────────────────────────────────────

export default function SummaryTab({ theme }) {
  return (
    <>
      <ServiceStatusSection theme={theme} />
      <SummaryHealthStrip theme={theme} />
      <HeadlineStats theme={theme} />
      <CronJobsSection theme={theme} />
    </>
  );
}
